import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, getSession, setSession } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import { ArrowLeft, FileCsv, FilePdf, Package, Truck, Lock, LockOpen, EnvelopeSimple } from "@phosphor-icons/react";

const PALETTE = ["#F97316", "#3B82F6", "#10B981", "#8B5CF6", "#F59E0B", "#EF4444"];

const closedText = (d) =>
  d.closed_by === "auto" ? "closed automatically" : d.closed_by === "manual" ? "closed manually" : `closed by ${d.closed_by}`;

const StatusBadge = ({ status }) =>
  status === "closed" ? (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-2 py-1">
      <Lock size={12} weight="fill" /> Closed
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1">
      <LockOpen size={12} weight="fill" /> Open
    </span>
  );

export default function Report() {
  const { date } = useParams();
  const [report, setReport] = useState(null);
  const [sending, setSending] = useState(false);

  const onResend = async () => {
    setSending(true);
    try {
      await api.resendReport(date);
      toast.success("Report email sent");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const session = getSession();
    if (session?.role === "guest" && session.freshLink) {
      // one-time record that an emailed report link was opened (visible on admin Activity page)
      api.logReportView(date);
      setSession({ ...session, freshLink: false });
    }
    api
      .getReport(date)
      .then(setReport)
      .catch(() => toast.error("Failed to load report"));
  }, [date]);

  if (!report) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center text-slate-500" data-testid="report-loading">
        Loading report...
      </div>
    );
  }

  const comparison = report.warehouses.map((w) => ({
    name: w.warehouse.name,
    packages: w.total_packages,
  }));
  const isDemo = report.warehouses.some((w) => w.day.demo);
  const doralWh = report.warehouses.find((w) => w.warehouse.id === "doral");
  const share = report.warehouses
    .filter((w) => w.total_packages > 0)
    .map((w) => ({ name: w.warehouse.name, value: w.total_packages }));

  return (
    <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="report-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <Link
            to="/archives"
            data-testid="back-to-archives"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors duration-200"
          >
            <ArrowLeft size={16} weight="bold" /> Archives
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-1">
            Daily Report
          </h1>
          <p className="text-base md:text-lg text-slate-500 mt-1 flex items-center gap-2" data-testid="report-date">
            {report.date}
            {isDemo && (
              <span
                data-testid="demo-badge"
                className="inline-flex items-center text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 rounded px-2 py-0.5"
              >
                Demo report — not real numbers
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {getSession()?.role === "admin" && (
            <button
              data-testid="resend-email-btn"
              onClick={onResend}
              disabled={sending}
              className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 rounded-md font-semibold px-4 py-2 transition-colors duration-200"
            >
              <EnvelopeSimple size={18} weight="duotone" /> {sending ? "Sending..." : "Resend Email"}
            </button>
          )}
          <button
            type="button"
            data-testid="download-csv-btn"
            onClick={() => api.downloadFile(api.csvUrl(date), `kpi-report-${date}.csv`).catch(() => toast.error("Download failed — please try again"))}
            className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md font-semibold px-4 py-2 transition-colors duration-200"
          >
            <FileCsv size={18} weight="duotone" /> CSV
          </button>
          <button
            type="button"
            data-testid="download-pdf-btn"
            onClick={() => api.downloadFile(api.pdfUrl(date), `kpi-report-${date}.pdf`).catch(() => toast.error("Download failed — please try again"))}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-md font-semibold px-4 py-2 transition-colors duration-200"
          >
            <FilePdf size={18} weight="duotone" /> PDF
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4" data-testid="report-processing-summary">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
          End-of-day processing (entered by Doral for both locations)
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center justify-between border border-slate-200 rounded-md px-4 py-3 bg-slate-50">
            <div>
              <div className="text-sm font-bold text-slate-900">Rocketship</div>
              <div className="text-xs text-slate-500">
                Shipping # <span className="font-semibold text-slate-700" data-testid="report-rs-shipping">{doralWh?.rocketship_shipping || "—"}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading text-2xl font-extrabold text-emerald-600" data-testid="report-rs-processed">{doralWh?.rocketship_processed ?? 0}</div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">processed</div>
            </div>
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-md px-4 py-3 bg-slate-50">
            <div>
              <div className="text-sm font-bold text-slate-900">RCJ</div>
              <div className="text-xs text-slate-500">
                Shipping # <span className="font-semibold text-slate-700" data-testid="report-rcj-shipping">{doralWh?.rcj_shipping || "—"}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading text-2xl font-extrabold text-emerald-600" data-testid="report-rcj-processed">{doralWh?.rcj_processed ?? 0}</div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">processed</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4" data-testid="report-pallets-summary">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
          Total pallets sent
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {report.warehouses.map((w) => (
            <div key={w.warehouse.id} className="flex items-center justify-between border border-slate-200 rounded-md px-4 py-3 bg-slate-50">
              <div className="text-sm font-bold text-slate-900">{w.warehouse.name}</div>
              <div className="flex items-center gap-6 text-right">
                <div>
                  <div className="font-heading text-2xl font-extrabold text-emerald-600" data-testid={`report-pallets-${w.warehouse.id}`}>{w.pallets ?? 0}</div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">pallets</div>
                </div>
                <div>
                  <div className="font-heading text-2xl font-extrabold text-emerald-600" data-testid={`report-single-items-${w.warehouse.id}`}>{w.single_items ?? 0}</div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">single items</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="report-summary">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            <Package size={14} weight="duotone" /> Grand Total
          </div>
          <div className="font-heading text-3xl font-extrabold text-orange-500 mt-1" data-testid="report-grand-total">
            {report.grand_total}
          </div>
          <div className="text-xs font-semibold text-emerald-600" data-testid="report-grand-processed">
            {report.grand_processed} processed (Rocketship + RCJ)
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            <Truck size={14} weight="duotone" /> Deliveries
          </div>
          <div className="font-heading text-3xl font-extrabold text-slate-900 mt-1" data-testid="report-total-deliveries">
            {report.total_deliveries}
          </div>
        </div>
        {report.warehouses.map((w) => (
          <div key={w.warehouse.id} className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{w.warehouse.name}</div>
            <div className="flex items-end justify-between mt-1">
              <div className="font-heading text-3xl font-extrabold text-slate-900" data-testid={`report-total-${w.warehouse.id}`}>
                {w.total_packages}
              </div>
              <StatusBadge status={w.day.status} />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="report-charts">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4">
            Warehouse comparison — total packages
          </div>
          <div className="h-64" data-testid="chart-comparison">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparison}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} />
                <Tooltip />
                <Bar dataKey="packages" radius={[4, 4, 0, 0]}>
                  {comparison.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4">
            Package share by warehouse
          </div>
          <div className="h-64" data-testid="chart-share">
            {share.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">No packages logged</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={share} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                    {share.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {report.warehouses.map((w) => {
        const carrierData = Object.entries(w.carrier_totals).map(([carrier, packages]) => ({ carrier, packages }));
        return (
          <section
            key={w.warehouse.id}
            className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden"
            data-testid={`report-section-${w.warehouse.id}`}
          >
            <div className="px-5 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{w.warehouse.name}</h2>
                <div className="text-sm text-slate-500">
                  {w.deliveries.length} {w.deliveries.length === 1 ? "delivery" : "deliveries"} · {w.total_packages} received · est. overflow {w.estimated_overflow}
                  {w.day.closed_at && ` · ${closedText(w.day)}`}
                </div>
              </div>
              <StatusBadge status={w.day.status} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
              <div className="lg:col-span-3 overflow-x-auto">
                {w.deliveries.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-sm" data-testid={`report-empty-${w.warehouse.id}`}>
                    No deliveries logged for this warehouse.
                  </div>
                ) : (
                  <table className="w-full text-sm text-left text-slate-600">
                    <thead className="text-xs uppercase bg-slate-50 text-slate-500 border-b border-slate-200 font-semibold tracking-wider">
                      <tr>
                        <th className="px-5 py-3">Time</th>
                        <th className="px-5 py-3">Carrier</th>
                        <th className="px-5 py-3">Packages</th>
                        <th className="px-5 py-3">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {w.deliveries.map((d) => (
                        <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`report-row-${d.id}`}>
                          <td className="px-5 py-2.5 font-semibold text-slate-900">{d.time}</td>
                          <td className="px-5 py-2.5">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                              {d.carrier}
                            </span>
                          </td>
                          <td className="px-5 py-2.5 font-semibold text-slate-900">{d.packages}</td>
                          <td className="px-5 py-2.5 text-slate-500">{d.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-orange-50 border-t border-orange-200">
                        <td className="px-5 py-3 font-bold text-slate-900" colSpan={2}>TOTAL RECEIVED</td>
                        <td className="px-5 py-3 font-bold text-orange-600" data-testid={`report-table-total-${w.warehouse.id}`}>
                          {w.total_packages}
                        </td>
                        <td></td>
                      </tr>
                      <tr className="bg-blue-50 border-t border-blue-100">
                        <td className="px-5 py-3 font-bold text-slate-900" colSpan={2}>EST. OVERFLOW</td>
                        <td className="px-5 py-3 font-bold text-blue-600" data-testid={`report-table-overflow-${w.warehouse.id}`}>
                          {w.estimated_overflow}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
              <div className="lg:col-span-2 border-t lg:border-t-0 lg:border-l border-slate-200 p-5">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
                  Packages per carrier
                </div>
                <div className="h-56" data-testid={`chart-carrier-${w.warehouse.id}`}>
                  {carrierData.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-slate-400 text-sm">No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={carrierData} layout="vertical" margin={{ left: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                        <YAxis type="category" dataKey="carrier" width={96} tick={{ fontSize: 11, fill: "#334155" }} />
                        <Tooltip />
                        <Bar dataKey="packages" radius={[0, 4, 4, 0]}>
                          {carrierData.map((_, i) => (
                            <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
            {w.captures.length > 0 && (
              <div className="border-t border-slate-200 p-5" data-testid={`osd-section-${w.warehouse.id}`}>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
                  OS&D captures — dispute evidence
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {w.captures.map((c) => (
                    <figure key={c.id} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`osd-card-${c.id}`}>
                      {(c.content_type || "").startsWith("video/") ? (
                        <video
                          src={api.captureImageUrl(c.id)}
                          controls
                          className="w-full h-36 object-cover bg-slate-900"
                          data-testid={`osd-video-${c.id}`}
                        />
                      ) : (
                        <a href={api.captureImageUrl(c.id)} target="_blank" rel="noopener noreferrer">
                          <img
                            src={api.captureImageUrl(c.id)}
                            alt={c.note || "OS&D capture"}
                            className="w-full h-36 object-cover bg-slate-100"
                            data-testid={`osd-image-${c.id}`}
                          />
                        </a>
                      )}
                      <figcaption className="p-2">
                        <div className="text-sm font-semibold text-slate-900 break-words">{c.note || "(no note)"}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          by {c.uploaded_by} · {new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            )}
            {w.overflow_captures?.length > 0 && (
              <div className="border-t border-slate-200 p-5" data-testid={`overflow-media-section-${w.warehouse.id}`}>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
                  Estimated overflow media — packages remaining
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {w.overflow_captures.map((c) => (
                    <figure key={c.id} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`overflow-card-${c.id}`}>
                      {(c.content_type || "").startsWith("video/") ? (
                        <video
                          src={api.captureImageUrl(c.id)}
                          controls
                          className="w-full h-36 object-cover bg-slate-900"
                          data-testid={`overflow-video-${c.id}`}
                        />
                      ) : (
                        <a href={api.captureImageUrl(c.id)} target="_blank" rel="noopener noreferrer">
                          <img
                            src={api.captureImageUrl(c.id)}
                            alt={c.note || "Overflow capture"}
                            className="w-full h-36 object-cover bg-slate-100"
                            data-testid={`overflow-image-${c.id}`}
                          />
                        </a>
                      )}
                      <figcaption className="p-2">
                        <div className="text-sm font-semibold text-slate-900 break-words">{c.note || "(no note)"}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          by {c.uploaded_by} · {new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            )}
          </section>
        );
      })}
    </main>
  );
}
